import { defineComponent, computed, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import {
  NavBar as VanNavBar,
  Icon as VanIcon,
  Sticky as VanSticky,
  Row as VanRow,
  Col as VanCol,
  Image as VanImage,
  ActionSheet as VanActionSheet,
  showImagePreview,
  showToast,
} from 'vant'
import {
  getApiV1CommunityDetail,
  getApiV1LotterySearch49208,
  getApiV1LotteryHkmarksixLive,
  getApiV1LotteryAocaiLive,
  postApiV1ForumCommentLike,
  getApiV1UserByIdProfile,
  postApiV1UserFollow,
} from '@/api/sdk.gen'
import type { LiveLotteryData, LotteryRecord } from '@/api/types.gen'
import { mapApiPost, type ForumPost } from '@/stores/forum-store'
import { useAuthStore } from '@/stores/auth-store'
import { useFollowStore } from '@/stores/follow-store'
import { useSeo, useSiteBrand } from '@/composables/use-seo'
import { FansTopBanner } from '@/components/fans-top-banner'
import UserAvatar from '@/components/user-avatar'
import { renderRecordBalls } from '@/components/lottery-ball-row'
import { CommentSection, SOCIAL_SCENE } from '@/components/social-section'
import imgCompeteTrophy from '@/assets/forum/compete-trophy.svg'
import imgCompeteCoins from '@/assets/forum/compete-coins.svg'
import iconShare from '@/assets/forum/share.png'

// 参赛信息（由 API 的 compete_data 派生成 UI 模型）
interface CompeteDetail {
  subCompeteTypeName: string
  value: string[]
  successValues: string[]
}
interface CompeteInfo {
  number: string
  competeTypeName: string
  detailList: CompeteDetail[]
  success: number | null // null=等待开奖 / 0=未中 / >0=中奖金额
  winTips: string
}

// sub_compete_type_id → 名称 / 所属类型（用于后端新建帖回显缺名称时兜底）
const SUB_COMPETE_MAP: Record<number, { name: string; typeName: string; typeId: number }> = {
  4: { name: '三肖中特', typeName: '特肖', typeId: 1 },
  5: { name: '二肖中特', typeName: '特肖', typeId: 1 },
  6: { name: '一肖中特', typeName: '特肖', typeId: 1 },
  7: { name: '一码中特', typeName: '特肖', typeId: 1 },
  8: { name: '12码中特', typeName: '特码', typeId: 2 },
  9: { name: '8码中特', typeName: '特码', typeId: 2 },
  10: { name: '4码中特', typeName: '特码', typeId: 2 },
  11: { name: '1码中特', typeName: '特码', typeId: 2 },
  12: { name: '8码', typeName: '平码', typeId: 3 },
}
const COMPETE_TYPE_NAME: Record<number, string> = { 1: '特肖', 2: '特码', 3: '平码' }

// 后端读取格式：大多数老帖是 camelCase（detailList/subCompeteTypeName/…），
// 刚发布的新帖回显可能是 snake_case 且字段不完整。兼容两种形状。
type RawCompeteDetail = {
  subCompeteTypeId?: number
  sub_compete_type_id?: number
  subCompeteTypeName?: string
  sub_compete_type_name?: string
  value?: string
  successValue?: string
  success_value?: string
}
type RawCompeteData = {
  number?: number
  competeTypeId?: number
  compete_type_id?: number
  competeTypeName?: string
  compete_type_name?: string
  status?: number
  success?: number
  winTips?: string
  win_tips?: string
  detailList?: RawCompeteDetail[]
  detail_list?: RawCompeteDetail[]
}

function buildCompeteFromApi(post: ForumPost): CompeteInfo | null {
  const cd = post.raw.compete_data as RawCompeteData | null | undefined
  if (!cd) return null
  const rawList = cd.detailList ?? cd.detail_list ?? []
  if (!rawList.length) return null

  const detailList = rawList.map<CompeteDetail>((d) => {
    const parts = String(d.value ?? '')
      .split('-')
      .map((v) => v.trim())
      .filter(Boolean)
    // 后端用半角逗号分隔（如 "05,26,41"），兼容全角逗号与 - 容错
    const successValues = String(d.successValue ?? d.success_value ?? '')
      .split(/[,，-]/)
      .map((v) => v.trim())
      .filter(Boolean)
    const subId = d.subCompeteTypeId ?? d.sub_compete_type_id
    const name =
      d.subCompeteTypeName ??
      d.sub_compete_type_name ??
      (subId ? (SUB_COMPETE_MAP[subId]?.name ?? '') : '')
    return {
      subCompeteTypeName: name,
      value: parts,
      successValues,
    }
  })

  const firstSubId = rawList[0]?.subCompeteTypeId ?? rawList[0]?.sub_compete_type_id
  const competeTypeName =
    cd.competeTypeName ??
    cd.compete_type_name ??
    COMPETE_TYPE_NAME[cd.competeTypeId ?? cd.compete_type_id ?? 0] ??
    (firstSubId ? (SUB_COMPETE_MAP[firstSubId]?.typeName ?? '') : '')

  // 用顶层 compete_status 判断：1=未开奖→null / 2=已中奖→金额 / 3=未中奖→0
  const cs = post.raw.compete_status
  let success: number | null = null
  if (cs === 2) success = cd.success && cd.success > 0 ? cd.success : 0
  else if (cs === 3) success = 0
  return {
    number: String(cd.number ?? post.periodStr),
    competeTypeName,
    detailList,
    success,
    winTips: post.raw.win_tips || ''
  }
}

/** 竞猜结果样式：中奖金额 > 0 为已中，0 为未中，null 为未开奖 */
function competeStatusClass(success: number | null): string {
  if (success) return 'status-win'
  if (success === 0) return 'status-lose'
  return ''
}

/** 竞猜结果文案，口径同 competeStatusClass */
function competeStatusText(success: number | null): string {
  if (success) return `中${success}元`
  if (success === 0) return '未中奖'
  return '等待开奖'
}

export default defineComponent({
  name: 'ForumDetailPage',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const goBack = useSmartBack()

    const authStore = useAuthStore()
    const followStore = useFollowStore()

    const postId = computed(() => Number(route.params.id))
    const post = ref<ForumPost | undefined>(undefined)
    const concernState = computed(() => followStore.isFollowing(post.value?.userId ?? ''))

    // SEO：论坛帖子详情页，title/description 随帖子内容动态变化（站点名取后台配置）
    const brand = useSiteBrand()
    useSeo({
      title: () => `${post.value?.title || '六合论坛帖子'}_六合论坛 - ${brand.value}`,
      description: () => {
        const text = (post.value?.description || '').replace(/\s+/g, ' ').trim()
        if (!text) return `${brand.value}六合论坛，高手心水交流与资料分享。`
        return text.length > 150 ? `${text.slice(0, 150)}…` : text
      },
      keywords: () => `${post.value?.title || '六合论坛'},高手心水,资料分享,六合论坛,${brand.value}`,
      ogType: 'article',
    })

    async function fetchPost(id: number) {
      if (!id) return
      const res = await getApiV1CommunityDetail({ query: { id } })
      const raw = res.data?.data
      post.value = raw ? mapApiPost(raw) : undefined
      if (post.value) {
        postThumb.value = raw?.supported ?? false
        postThumbCount.value = raw?.thumb_up_count ?? raw?.likes ?? 0
        commentCount.value = post.value.commentCount
        fetchLotteryRecord(post.value)
        fetchNextDraw(post.value)
        if (post.value.userId) fetchAuthorProfile(post.value.userId)
      }
    }

    const authorStats = reactive({
      thumbUpCount: 0,
      fansCount: 0,
      concernCount: 0,
      score: 0,
      clickCount: 0,
    })
    const formatWan = (n: number) => (n >= 10000 ? `${(n / 10000).toFixed(2)}万` : String(n))

    async function fetchAuthorProfile(userId: string) {
      const uid = Number(userId)
      if (!uid) return
      try {
        const res = await getApiV1UserByIdProfile({ path: { id: uid } })
        const profile = res.data?.data
        if (profile) {
          // profile.isFollowing 是当前作者关注状态的权威值，同步到全局 follow-store
          if (typeof profile.isFollowing === 'boolean') {
            followStore.setFollowed(uid, profile.isFollowing)
          }
          authorStats.fansCount = profile.fanCount ?? 0
          authorStats.concernCount = profile.followCount ?? 0
          authorStats.thumbUpCount = profile.userTotalSupport ?? 0
          authorStats.clickCount = profile.userTotalVisit ?? 0
        }
      } catch {
        /* ignore */
      }
    }

    async function toggleConcern() {
      if (!authStore.isLoggedIn) {
        router.push('/login')
        return
      }
      const uid = Number(post.value?.userId)
      if (!uid) return
      const prev = followStore.isFollowing(uid)
      followStore.setFollowed(uid, !prev)
      try {
        await postApiV1UserFollow({ body: { follow_user_id: uid } })
      } catch {
        followStore.setFollowed(uid, prev)
        showToast('操作失败')
      }
    }

    // 参赛信息：从 API 的 compete_data 派生，非参赛帖为 null
    const compete = computed<CompeteInfo | null>(() => {
      const p = post.value
      if (!p?.isContest) return null
      return buildCompeteFromApi(p)
    })

    // 顶部开奖结果区：优先匹配帖子 periodStr 对应的期次，否则回退到当年最新一期
    const lotteryRecord = ref<LotteryRecord | null>(null)
    async function fetchLotteryRecord(p: ForumPost) {
      const region: 'hk' | 'aocai' = p.lotteryType === 'hk' ? 'hk' : 'aocai'
      const year = new Date().getFullYear()
      try {
        // pageSize=200 足以覆盖一年最多约 153 期（港彩周三/周六/周日）
        const res = await getApiV1LotterySearch49208({
          query: { region, year, pageNum: 1, pageSize: 200, sort: 2 },
        })
        const list = res.data?.data?.recordList ?? []
        const matched = list.find((r) => r.periodStr === p.periodStr)
        lotteryRecord.value = matched ?? list[0] ?? null
      } catch {
        lotteryRecord.value = null
      }
    }

    // 下一期开奖信息（footer 显示「第N期 YYYY/MM/DD HH:mm 星期X」）
    const nextDraw = ref<LiveLotteryData | null>(null)
    async function fetchNextDraw(p: ForumPost) {
      try {
        const res =
          p.lotteryType === 'hk'
            ? await getApiV1LotteryHkmarksixLive()
            : await getApiV1LotteryAocaiLive()
        nextDraw.value = res.data?.data ?? null
      } catch {
        nextDraw.value = null
      }
    }
    // "21点32分" → "21:32"（不做时区转换，纯字符串替换）
    const lotteryFooter = computed(() => {
      const n = nextDraw.value
      if (!n) return lotteryRecord.value?.lotteryTime ?? ''
      const time = (n.next_draw_time ?? '').replace(/(?<!\d)(\d+)点(\d+)分/, '$1:$2')
      const parts = [
        n.next_issue ? `第${n.next_issue}期` : '',
        n.next_draw_date ?? '',
        time,
        n.next_draw_weekday ? `星期${n.next_draw_weekday}` : '',
      ].filter(Boolean)
      return parts.join(' ')
    })
    onMounted(() => {
      fetchPost(postId.value)
      if (authStore.isLoggedIn) followStore.fetchFollowedList()
    })
    watch(
      () => postId.value,
      (id) => fetchPost(id),
    )

    // 评论区：评论列表、热门评论、发表/回复弹窗等统一收敛在 CommentSection 内
    const commentCount = ref(0)
    const commentSectionRef = ref<{ openCommentPopup: () => void } | null>(null)
    function openCommentPopup() {
      if (!authStore.isLoggedIn) {
        router.push('/login')
        return
      }
      commentSectionRef.value?.openCommentPopup()
    }

    // 底部点赞（接入 /api/v1/forum/comment/like），分享暂留占位
    const postThumb = ref(false)
    const postThumbCount = ref(0)
    async function togglePostThumb() {
      if (!authStore.isLoggedIn) {
        router.push('/login')
        return
      }
      const pid = postId.value
      if (!pid) return
      const prev = postThumb.value
      postThumb.value = !prev
      postThumbCount.value = Math.max(0, postThumbCount.value + (prev ? -1 : 1))
      try {
        await postApiV1ForumCommentLike({ body: { id: pid, is_support: postThumb.value ? 1 : 0 } })
      } catch {
        postThumb.value = prev
        postThumbCount.value = Math.max(0, postThumbCount.value + (prev ? 1 : -1))
        showToast('操作失败')
      }
    }
    const onShare = () => showToast('分享功能开发中')

    // 跳转作者历史帖子页（query 与 49208 /corpusdetaillist 对齐）
    const authorPath = computed(() => `/user/${post.value?.userId ?? ''}`)
    const authorPostsPath = computed(() => {
      const t = encodeURIComponent(post.value?.username ?? '作者')
      const lt = post.value?.lotteryType === 'hk' ? 1 : 2
      return `/forum/user-posts?userId=${post.value?.userId ?? ''}&title=${t}&type=3&webtitle=${t}&lotteryType=${lt}`
    })

    // 右上角「更多」菜单：仅保留「查看用户主页」「历史帖子」（49208 同款其余按钮本期不要）
    const moreShow = ref(false)
    const moreActions = [{ name: '查看用户主页' }, { name: '历史帖子' }]
    function onMoreSelect(action: { name: string }) {
      moreShow.value = false
      const uid = post.value?.userId
      if (!uid) return
      if (action.name === '查看用户主页') {
        router.push(authorPath.value)
      } else if (action.name === '历史帖子') {
        router.push(authorPostsPath.value)
      }
    }

    return () => (
      <div class="corpusdetail-msg overflow-x-hidden">
        {/* ===== 顶部导航栏 ===== */}
        <VanSticky>
          <VanNavBar title="详情" onClickLeft={() => goBack()}>
            {{
              left: () => <VanIcon name="arrow-left" color="#333" size="16" />,
              right: () => (
                <VanIcon
                  class="detail-nav-right-icon"
                  name="ellipsis"
                  color="#07c160"
                  size="16"
                  onClick={() => (moreShow.value = true)}
                />
              ),
            }}
          </VanNavBar>
        </VanSticky>

        {/* ===== 开奖结果区：6+1 号码 + 生肖/五行（对齐 article-detail 的 border-ws 结构） ===== */}
        {lotteryRecord.value && (
          <div class="block-lottery">
            <div class="border-ws border-box">
              <div class="border-box-body">
                <div class="border-box-body-hd">
                  <VanRow class="flex justify-between">
                    <VanCol span={12}>
                      <div class="text-left">
                        第<span>{lotteryRecord.value.period}</span>期开奖结果
                      </div>
                    </VanCol>
                    <VanCol span={12}>
                      <div class="text-right">
                        {/* `.text-right a` 与 `.text-right span` 同一套样式 */}
                        <RouterLink
                          class="btn-reset"
                          to={{ path: '/history', query: { lotteryType: post.value?.lotteryType } }}
                        >
                          <span>查看历史记录</span>
                        </RouterLink>
                      </div>
                    </VanCol>
                  </VanRow>
                </div>
                <div class="border-box-body-bd">
                  {renderRecordBalls(lotteryRecord.value.numberList ?? [])}
                </div>
                <div class="border-box-body-ft">{lotteryFooter.value}</div>
              </div>
            </div>
          </div>
        )}

        {/* ===== 帖子主体 ===== */}
        <div class="detail-body">
          {/* 用户卡片区 sticky 吸顶 */}
          <VanSticky offsetTop={46}>
            <div class="detail-body-box">
              <div class="cell-top clearfix">
                <div class="user-img">
                  {/* 头像下面挂着关注按钮：链接只包头像，关注按钮放在链接外面 */}
                  <div class="left-image">
                    <RouterLink class="btn-reset" to={authorPath.value}>
                      <UserAvatar size="1.2rem" src={post.value?.headPic} />
                    </RouterLink>
                    {/* 原站 <follow> 组件通过 slot="img-ft" 挂在 .left-image 内 */}
                    <div class="m-follow">
                      <button
                        type="button"
                        class={['add-follow btn-reset', concernState.value ? 'ok' : '']}
                        onClick={toggleConcern}
                      >
                        {concernState.value ? '已关注' : '关注'}
                      </button>
                    </div>
                  </div>
                  <RouterLink class="user-info btn-reset block" to={authorPath.value}>
                    <div class="name">{post.value?.username || '用户名'}</div>
                    <div class="time">{post.value?.createTime || '—'}</div>
                  </RouterLink>
                </div>
                <div class="right-tool">
                  <RouterLink class="editor-btn btn-reset" to={authorPostsPath.value}>
                    历史帖子
                  </RouterLink>
                </div>
              </div>
              <VanRow class="interaction-info">
                <VanCol span={4}>
                  <div class="num">{formatWan(authorStats.thumbUpCount)}</div>
                  <div class="label">获赞</div>
                </VanCol>
                <VanCol span={5}>
                  <div class="num">{formatWan(authorStats.fansCount)}</div>
                  <div class="label">粉丝</div>
                </VanCol>
                <VanCol span={5}>
                  <div class="num">{formatWan(authorStats.concernCount)}</div>
                  <div class="label">关注</div>
                </VanCol>
                <VanCol span={5}>
                  <div class="num">{formatWan(authorStats.score)}</div>
                  <div class="label">成长值</div>
                </VanCol>
                <VanCol span={5}>
                  <div class="num">{formatWan(authorStats.clickCount)}</div>
                  <div class="label">阅读数</div>
                </VanCol>
              </VanRow>
            </div>
          </VanSticky>

          {/* 粉丝排行榜入口（作者上榜时显示） */}
          <FansTopBanner userId={post.value?.userId} />

          {/* 帖子标题 */}
          <div class="content-box has-read">{post.value?.title || '帖子标题（骨架占位）'}</div>

          {/* 帖子正文 */}
          <div class="pagebox">
            {post.value?.raw.picture?.pictureUrl && (
              <VanImage
                src={post.value.raw.picture.pictureUrl}
                fit="cover"
                width="100%"
                height="100%"
              />
            )}
            <div
              class="desc-body-box"
              innerHTML={post.value?.description || '帖子正文（后续接入富文本渲染）'}
            />
            {post.value?.pictureList && post.value.pictureList.length > 0 && (
              <div class="images">
                <VanRow class="img-box-3 mx-[0.2rem]" gutter={10}>
                  {/* 同一张图可能出现两次，key 用「地址 + 位置」 */}
                  {post.value.pictureList.map((src, i) => (
                    <VanCol
                      key={`${src}-${i}`}
                      span={8}
                      onClick={() =>
                        showImagePreview({
                          images: post.value!.pictureList!,
                          startPosition: i,
                          closeable: true,
                        })
                      }
                    >
                      <div class="image-body">
                        <VanImage src={src} fit="cover" width="100%" height="100%" />
                      </div>
                    </VanCol>
                  ))}
                </VanRow>
              </div>
            )}
          </div>

          {/* ===== 参赛信息框 ===== */}
          {compete.value && (
            <div class="compete-box">
              <div class="title">
                <img src={imgCompeteTrophy} alt="" />
                参赛贴&nbsp;&nbsp;第{compete.value.number}期{compete.value.competeTypeName}
              </div>
              <div class="tab">
                <div class="left">
                  {compete.value.detailList.map((item) => (
                    <div class="item" key={item.subCompeteTypeName}>
                      <span class="whitespace-nowrap shrink-0">{item.subCompeteTypeName}：</span>
                      <div class="t-value">
                        {item.value.map((v) => (
                          <span key={v} class={item.successValues.includes(v) ? 'active' : ''}>
                            -{v}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div class="right">
                  <div>
                    <img src={imgCompeteCoins} alt="" width="40" height="35" />
                  </div>
                  <span class={competeStatusClass(compete.value.success)}>
                    {competeStatusText(compete.value.success)}
                  </span>
                </div>
              </div>
              <div class="fot">{compete.value.winTips || '\u00A0'}</div>
            </div>
          )}

          {/* 礼物榜（彩金/小红花统计） */}
          {/* 该功能目前暂未开发，先屏蔽 */}
          {/* <div class="fans-top">
            <img class="title" src={imgGiftTitle} alt="礼物榜" />
            <span class="huanghua">
              <img src={imgGiftAmount} alt="彩金" />
              <label>{rewardAmount.value}</label>
            </span>
            <span class="honghua">
              <img src={imgGiftCount} alt="小红花" />
              <label>{rewardCount.value}</label>
            </span>
            <span class="more" onClick={() => router.push('/my/top-gift')}>
              查看更多 &gt;
            </span>
          </div> */}
        </div>

        {/* ===== 评论区（统一组件：热门 + 最新 + 发表/回复弹窗） ===== */}
        {postId.value > 0 && (
          <CommentSection
            ref={commentSectionRef}
            scene={SOCIAL_SCENE.FORUM}
            targetId={postId.value}
            lotteryType={post.value?.lotteryType === 'hk' ? 1 : 2}
            authorId={post.value?.userId}
            enableOnlyAuthor
            enableOrderToggle
            commentCount={commentCount.value}
            onCountUpdate={(n: number) => (commentCount.value = n)}
            hideToolBottom
          />
        )}

        {/* ===== 底部操作栏（对齐原站 .tool-bottom，自有点赞接口） ===== */}
        <div class="tool-bottom">
          <div class="tool-bottom-body">
            <VanRow>
              <VanCol span={16}>
                <button type="button" class="left btn-reset" onClick={openCommentPopup}>
                  {/* 原来是 label，样式在 `.tool-bottom-body .left label` 上，挪到类上 */}
                  <span class="block ml-[0.3rem] text-[0.24rem]">想说点什么...</span>
                </button>
              </VanCol>
              <VanCol span={8}>
                <VanRow class="right">
                  <VanCol span={6} onClick={togglePostThumb}>
                    <VanIcon
                      name={postThumb.value ? 'good-job' : 'good-job-o'}
                      color="#999999"
                      size="18"
                      class={postThumb.value ? 'act' : ''}
                    />
                    <div class="label">{formatWan(postThumbCount.value)}</div>
                  </VanCol>
                  <VanCol span={6} onClick={openCommentPopup}>
                    <VanIcon name="chat-o" color="#999999" size="18" />
                    <div class="label">{formatWan(commentCount.value)}</div>
                  </VanCol>
                  <VanCol span={6} onClick={onShare}>
                    <img src={iconShare} alt="分享" />
                    <div class="label">分享</div>
                  </VanCol>
                </VanRow>
              </VanCol>
            </VanRow>
          </div>
        </div>

        {/* ===== 右上角「更多」菜单 ===== */}
        <VanActionSheet
          v-model:show={moreShow.value}
          actions={moreActions}
          cancelText="取消"
          closeOnClickAction
          onSelect={onMoreSelect}
        />
      </div>
    )
  },
})
